import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { LayoutDashboard, Plus } from "lucide-react";
import { useEffect, useRef } from "react";
import { ConfirmButton } from "@/components/confirm-button";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { queryClient, trpc } from "@/lib/trpc";
import { formatRelativeDate } from "@/lib/utils";

const skeletonKeys = ["a", "b", "c"];

export function BoardsPage() {
	const navigate = useNavigate();
	const boards = useQuery(trpc.boards.list.queryOptions());
	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: trpc.boards.list.queryKey() });

	const create = useMutation(
		trpc.boards.create.mutationOptions({
			onSuccess: async (board) => {
				track("board.created_from", {
					board_id: board.id,
					from:
						(boards.data?.length ?? 0) === 0 ? "empty_state" : "boards_list",
					existing_boards: boards.data?.length ?? 0,
				});
				await invalidate();
				await navigate({
					to: "/boards/$boardId",
					params: { boardId: board.id },
				});
			},
		})
	);
	const remove = useMutation(
		trpc.boards.delete.mutationOptions({ onSuccess: invalidate })
	);

	// Once per visit: how many boards the list showed.
	const listed = useRef(false);
	const boardCount = boards.data?.length;
	useEffect(() => {
		if (boardCount === undefined || listed.current) {
			return;
		}
		listed.current = true;
		track("board_list.viewed", { boards: boardCount });
		if (boardCount === 0) {
			track("onboarding.empty_state_viewed", { surface: "boards" });
		}
	}, [boardCount]);

	return (
		<div className="mx-auto flex max-w-6xl flex-col gap-6 p-8">
			<header className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="font-display font-extrabold text-2xl tracking-[-0.02em]">
						Boards
					</h1>
					<p className="text-muted-foreground text-sm">
						Mix plucks with prompts to generate new designs.
					</p>
				</div>
				<Button
					disabled={create.isPending}
					onClick={() => create.mutate(undefined)}
				>
					<Plus />
					New board
				</Button>
			</header>

			{boards.isPending && (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{skeletonKeys.map((k) => (
						<Skeleton className="h-28 rounded-xl" key={k} />
					))}
				</div>
			)}
			{boards.data && boards.data.length > 0 && (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{boards.data.map((board) => (
						<div
							className="flex flex-col gap-3 rounded-xl border bg-card p-4"
							key={board.id}
						>
							<Link
								className="flex items-start gap-3 hover:underline"
								params={{ boardId: board.id }}
								to="/boards/$boardId"
							>
								<LayoutDashboard className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
								<div className="min-w-0">
									<p className="truncate font-medium">{board.name}</p>
									<p className="text-muted-foreground text-xs">
										Updated {formatRelativeDate(board.updatedAt)}
									</p>
								</div>
							</Link>
							<div className="flex justify-end">
								<ConfirmButton
									confirmLabel="Delete board?"
									onConfirm={() => remove.mutate({ id: board.id })}
									size="sm"
									variant="ghost"
								>
									Delete
								</ConfirmButton>
							</div>
						</div>
					))}
				</div>
			)}
			{boards.data && boards.data.length === 0 && (
				<div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
					<p className="font-medium">No boards yet.</p>
					<p className="max-w-md text-muted-foreground text-sm">
						A board is a canvas: drop plucks on it, connect them to a prompt,
						and generate a new design from the mix.
					</p>
					<Button
						disabled={create.isPending}
						onClick={() => create.mutate(undefined)}
						variant="outline"
					>
						<Plus />
						Create your first board
					</Button>
				</div>
			)}
		</div>
	);
}
